import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Avatar,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  AddAPhoto,
  LocationCity,
  Email,
  CalendarToday,
  Pets,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { authService, getImageUrl, hasValidLocation } from '../../services/api';

interface Cat {
  _id: string;
  name: string;
  breed: string;
  imageUrl: string;
  location: { latitude: number; longitude: number };
  likes_pats: boolean;
  createdAt: string;
}

const UserProfile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  const [myCats, setMyCats] = useState<Cat[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editForm, setEditForm] = useState({
    username: '',
    city: '',
    password: '',
  });
  const [newProfilePicture, setNewProfilePicture] = useState<File | null>(null);
  const [newProfilePreview, setNewProfilePreview] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setEditForm({
        username: user.username,
        city: user.city,
        password: '',
      });
      fetchMyCats();
    }
  }, [user]);

  const fetchMyCats = async () => {
    try {
      const cats = await authService.getMyCats();
      setMyCats(cats);
    } catch (err) {
      console.error('Failed to fetch cats:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setNewProfilePicture(file);
      const reader = new FileReader();
      reader.onload = () => setNewProfilePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleEditSubmit = async () => {
    try {
      setError(null);
      const formData = new FormData();
      if (editForm.username !== user?.username) {
        formData.append('username', editForm.username);
      }
      if (editForm.city !== user?.city) {
        formData.append('city', editForm.city);
      }
      if (editForm.password) {
        formData.append('password', editForm.password);
      }
      if (newProfilePicture) {
        formData.append('profilePicture', newProfilePicture);
      }

      const updated = await authService.updateProfile(formData);
      updateUser(updated);
      setEditDialogOpen(false);
      setNewProfilePicture(null);
      setNewProfilePreview(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update profile');
    }
  };

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 64px)',
        py: 4,
        background: 'linear-gradient(180deg, #0f0f1a 0%, #1a1a2e 100%)',
      }}
    >
      <Container maxWidth="lg">
        {/* Profile Header */}
        <Paper
          sx={{
            p: 4,
            mb: 4,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'center', md: 'flex-start' },
            gap: 4,
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={getImageUrl(user.profilePicture)}
              sx={{
                width: 150,
                height: 150,
                border: '4px solid',
                borderColor: 'primary.main',
                boxShadow: '0 8px 32px rgba(124, 58, 237, 0.3)',
              }}
            >
              {user.username[0].toUpperCase()}
            </Avatar>
            <IconButton
              onClick={() => setEditDialogOpen(true)}
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              <EditIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              {user.username}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                justifyContent: { xs: 'center', md: 'flex-start' },
                mb: 2,
              }}
            >
              <Chip
                icon={<Email sx={{ fontSize: 16 }} />}
                label={user.email}
                variant="outlined"
              />
              <Chip
                icon={<LocationCity sx={{ fontSize: 16 }} />}
                label={user.city}
                variant="outlined"
              />
              <Chip
                icon={<Pets sx={{ fontSize: 16 }} />}
                label={`${myCats.length} cats`}
                color="primary"
              />
              {user.isAdmin && (
                <Chip label="Admin" color="warning" />
              )}
            </Box>
          </Box>
        </Paper>

        {/* My Cats */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          My Cats
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : myCats.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Pets sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              You haven't uploaded any cats yet
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/upload')}
              startIcon={<AddAPhoto />}
            >
              Upload Your First Cat
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {myCats.map((cat) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={cat._id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 40px rgba(124, 58, 237, 0.2)',
                    },
                  }}
                  onClick={() => navigate(`/cats/${cat._id}`)}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={getImageUrl(cat.imageUrl)}
                    alt={cat.name}
                  />
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold">
                      {cat.name}
                    </Typography>
                    {cat.breed && (
                      <Typography variant="body2" color="text.secondary">
                        {cat.breed}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Edit Profile Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src={newProfilePreview || getImageUrl(user.profilePicture)}
                sx={{ width: 100, height: 100 }}
              >
                {user.username[0]}
              </Avatar>
              <IconButton
                component="label"
                sx={{
                  position: 'absolute',
                  bottom: -4,
                  right: -4,
                  bgcolor: 'primary.main',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
                size="small"
              >
                <AddAPhoto fontSize="small" />
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleProfilePictureChange}
                />
              </IconButton>
            </Box>
          </Box>

          <TextField
            label="Username"
            fullWidth
            value={editForm.username}
            onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            label="City"
            fullWidth
            value={editForm.city}
            onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            label="New Password"
            type="password"
            fullWidth
            value={editForm.password}
            onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
            helperText="Leave blank to keep current password"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSubmit}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserProfile;
